import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import BannerAccountSecurity from '../components/bannerAccountSecurity';
import ModalAccountPinForm from '../components/modal/accountPinForm';
import ModalAddEmailConfirmation from '../components/modal/addEmail/confirmation';
import ModalAddEmailForm from '../components/modal/addEmail/form';
import ModalChangePasswordConfirmation from '../components/modal/changePassword/confirmation';
import ModalChangePasswordForm from '../components/modal/changePassword/form';
import ModalChangePasswordIntro from '../components/modal/changePassword/intro';
import ModalDismissConfirmation from '../components/modal/dismissConfirmation';
import { ViewType } from '../constants/parameters';
import { ModalFragmentProps } from '../constants/types';
import useAccountSecurityPromptContext from '../hooks/useAccountSecurityPromptContext';
import { AccountSecurityPromptActionType } from '../store/action';
import ModalState from '../store/modalState';

const renderBanner = (viewType: ViewType, isPromptDismissed: boolean, isFlowComplete: boolean) => {
  if (viewType !== ViewType.BANNER || isPromptDismissed || isFlowComplete) {
    return null;
  }

  return <BannerAccountSecurity />;
};

type ModalSchema = {
  innerFragment: React.FC<ModalFragmentProps>;
  canClickBackdropToClose: boolean;
};

const getModalSchema = (viewType: ViewType, modalState: ModalState): ModalSchema | null => {
  switch (modalState) {
    case ModalState.ADD_EMAIL_FORM:
      return {
        innerFragment: ModalAddEmailForm,
        canClickBackdropToClose: viewType === ViewType.BANNER
      };
    case ModalState.ADD_EMAIL_CONFIRMATION:
      return {
        innerFragment: ModalAddEmailConfirmation,
        canClickBackdropToClose: true
      };
    case ModalState.CHANGE_PASSWORD_INTRO:
      return {
        innerFragment: ModalChangePasswordIntro,
        canClickBackdropToClose: viewType === ViewType.BANNER
      };
    case ModalState.CHANGE_PASSWORD_FORM:
      return {
        innerFragment: ModalChangePasswordForm,
        canClickBackdropToClose: false
      };
    case ModalState.ACCOUNT_PIN_FORM:
    case ModalState.ACCOUNT_PIN_FORM_EXPIRED:
      return {
        innerFragment: ModalAccountPinForm,
        canClickBackdropToClose: false
      };
    case ModalState.CHANGE_PASSWORD_CONFIRMATION:
      return {
        innerFragment: ModalChangePasswordConfirmation,
        canClickBackdropToClose: true
      };
    case ModalState.ADD_EMAIL_DISMISS_CONFIRMATION:
    case ModalState.CHANGE_PASSWORD_DISMISS_CONFIRMATION:
      return {
        innerFragment: ModalDismissConfirmation,
        canClickBackdropToClose: false
      };
    default:
      return null;
  }
};

const AccountSecurityPromptContainer: React.FC = () => {
  const {
    state: { viewType, isPromptDismissed, isFlowComplete, modalState },
    dispatch
  } = useAccountSecurityPromptContext();

  /*
   * Component State
   */

  const [isModalVisible, setIsModalVisible] = useState(true);

  /*
   * Event Handlers
   */

  const closeModal = () => setIsModalVisible(false);

  /**
   * While it is typical to trigger a modal close `onHide` (a property of every
   * modal), we do not set the modal state to `NONE` in a handler attached to
   * that event, since doing so would remove the modal element from the DOM
   * immediately and prevent a close animation from running.
   *
   * To allow the animation to run before setting a `NONE` state, we attach
   * this function to the modal's `onExited` event.
   */
  const setModalStateNone = () => {
    dispatch({
      type: AccountSecurityPromptActionType.SET_MODAL_STATE,
      modalState: ModalState.NONE
    });
    // Reset the modal visible state (although the component itself will not be
    // rendered at this point).
    setIsModalVisible(true);
  };

  /*
   * Component Markup
   */

  // We retrieve a schema to manually render an outer modal element instead of
  // delegating the modal render to the specific modal page component. This is
  // to prevent unwanted animations on modal updates due to modal components
  // being added and removed from the DOM.
  const modalSchema = getModalSchema(viewType, modalState);

  return (
    <React.Fragment>
      {renderBanner(viewType, isPromptDismissed, isFlowComplete)}
      {modalSchema && (
        <Modal
          className='modal-modern'
          show={!isPromptDismissed && isModalVisible}
          onHide={closeModal}
          onExited={setModalStateNone}
          backdrop={modalSchema.canClickBackdropToClose ? undefined : 'static'}>
          <modalSchema.innerFragment closeModal={closeModal} />
        </Modal>
      )}
    </React.Fragment>
  );
};

export default AccountSecurityPromptContainer;
